import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Download, FileSpreadsheet, FileText, Loader2, Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { erpApi, formatApiErrorDetail } from "@/lib/erpApi";
import { useErpAuth } from "@/context/ErpAuthContext";
import { MONTHS_RO, DAY_LETTERS } from "./pontajConstants";

const displayName = (e) => `${e.nume || ""} ${e.prenume || ""}`.trim() || e.name || e.email;

// Oglindă read-only a modelului de program de lucru (identic cu PDF-ul exportat)
function programLines(emp, hours) {
  const norma = emp.norma_ore || 8;
  const ps = emp.program_start || "08:00";
  const pe = emp.program_sfarsit || "17:00";
  const pauza = emp.program_pauza !== false;
  const pzs = emp.pauza_start || "12:00";
  const pze = emp.pauza_sfarsit || "13:00";
  if (hours === norma) {
    if (pauza && norma === 8) return [`${ps}-${pzs}`, "pauza", `${pze}-${pe}`];
    return [`${ps}-${pe}`];
  }
  const [h, m] = ps.split(":");
  const end = String(parseInt(h) + hours).padStart(2, "0");
  return [`${ps}-${end}:${m || "00"}`];
}

export default function EvidentaOreTab() {
  const { hasPerm } = useErpAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [employees, setEmployees] = useState([]);
  const [data, setData] = useState({});
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const canExport = hasPerm("pontaj.export") || hasPerm("pontaj.vizualizare");

  const daysInMonth = new Date(year, month, 0).getDate();
  const holidayDays = Object.fromEntries(holidays.map((h) => [h.zi, h.nume]));
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;
  const isFutureMonth = year > now.getFullYear() || (year === now.getFullYear() && month > now.getMonth() + 1);
  const todayDate = now.getDate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = await erpApi.get(`/pontaj/${year}/${month}`);
      setEmployees(p.data.employees || []);
      setData(p.data.pontaj || {});
      setHolidays(p.data.holidays || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const isWeekend = (day) => {
    const d = new Date(year, month - 1, day).getDay();
    return d === 0 || d === 6;
  };
  const isHoliday = (day) => day in holidayDays;

  const dayInMonth = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr.slice(0, 10));
    if (isNaN(d)) return null;
    if (d.getFullYear() === year && d.getMonth() + 1 === month) return d.getDate();
    return null;
  };

  const empInMonth = (emp) => {
    const s = (emp.angajat_de_la || "").slice(0, 10);
    if (s) { const d = new Date(s); if (year < d.getFullYear() || (year === d.getFullYear() && month < d.getMonth() + 1)) return false; }
    const en = (emp.pontaj_pana_la || "").slice(0, 10);
    if (en) { const d = new Date(en); if (year > d.getFullYear() || (year === d.getFullYear() && month > d.getMonth() + 1)) return false; }
    return true;
  };

  const rows = employees
    .filter((e) => e.role !== "admin" && e.active !== false && e.in_pontaj !== false && empInMonth(e))
    .sort((a, b) => (parseInt(a.numar_contract) || 999999) - (parseInt(b.numar_contract) || 999999));

  // Returnează { type, lines?, code?, total_add } pentru o celulă
  const getCell = (emp, day) => {
    const hireDay = dayInMonth(emp.angajat_de_la);
    const exitDay = dayInMonth(emp.pontaj_pana_la);
    if ((hireDay && day < hireDay) || (exitDay && day > exitDay)) return { type: "x" };
    const weekend = isWeekend(day);
    const holiday = isHoliday(day);
    const val = data[emp.id]?.[String(day)]?.value;
    if (val === undefined || val === null) {
      if (weekend || holiday) return { type: "x" };
      const isFuture = isFutureMonth || (isCurrentMonth && day > todayDate);
      if (isFuture) return { type: "empty" };
      const norma = emp.norma_ore || 8;
      return { type: "work", lines: programLines(emp, norma), add: norma };
    }
    if (/^\d+$/.test(String(val))) {
      const h = parseInt(val);
      return { type: "work", lines: programLines(emp, h), add: h };
    }
    return { type: "code", code: val };
  };

  const totalFor = (emp) => {
    let tot = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      const c = getCell(emp, d);
      if (c.type === "work") tot += c.add;
    }
    return tot;
  };

  const goPrev = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  const goNext = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };
  const goCurrent = () => { setMonth(now.getMonth() + 1); setYear(now.getFullYear()); };

  const download = async (type) => {
    setExporting(true);
    try {
      const res = await erpApi.get(`/pontaj/export/evidenta-ore/${type}/${year}/${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `evidenta_ore_${year}_${String(month).padStart(2, "0")}.${type === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Export ${type.toUpperCase()} realizat`);
    } catch {
      toast.error("Export eșuat");
    } finally {
      setExporting(false);
    }
  };

  const codeClass = (code) => {
    const map = {
      CO: "bg-blue-100 text-blue-700", CM: "bg-orange-100 text-orange-700",
      CFP: "bg-yellow-100 text-yellow-700", NM: "bg-red-100 text-red-700",
      DEM: "bg-purple-100 text-purple-700", CNC: "bg-pink-100 text-pink-700",
      CD: "bg-stone-300 text-stone-700",
    };
    return map[code] || "bg-slate-100 text-slate-700";
  };

  return (
    <div className="space-y-4" data-testid="erp-evidenta-panel">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="icon" onClick={goPrev} data-testid="evidenta-prev-month" className="h-9 w-9"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="flex items-center gap-2 px-2 py-1 bg-blue-50 rounded-lg border border-blue-200">
            <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
              <SelectTrigger className="w-[130px] h-8 border-0 bg-transparent" data-testid="evidenta-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS_RO.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
              <SelectTrigger className="w-[84px] h-8 border-0 bg-transparent" data-testid="evidenta-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>{[now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="icon" onClick={goNext} data-testid="evidenta-next-month" className="h-9 w-9"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={goCurrent} data-testid="evidenta-current-month" className="text-[#2563EB]">Luna curentă</Button>
        </div>
        {canExport && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => download("excel")} disabled={exporting} data-testid="evidenta-export-excel" className="text-green-700 border-green-200 bg-green-50 hover:bg-green-100">
              <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
            </Button>
            <Button variant="outline" size="sm" onClick={() => download("pdf")} disabled={exporting} data-testid="evidenta-export-pdf" className="text-red-700 border-red-200 bg-red-50 hover:bg-red-100">
              {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />} PDF
            </Button>
          </div>
        )}
      </div>

      {/* Info banner */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 text-blue-800 text-sm" data-testid="evidenta-info-banner">
        <Info className="w-4 h-4 shrink-0" />
        <span>Oglindă automată a Pontajului. Pentru a modifica orele sau absențele, folosește tab-ul <strong>Pontaj</strong>.</span>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 p-3 bg-slate-50 rounded-lg text-xs" data-testid="evidenta-legend">
        <div className="flex items-center gap-1.5">
          <span className="inline-flex flex-col items-center justify-center h-9 px-1.5 rounded border border-green-300 bg-green-50 text-[7px] leading-tight text-green-800">
            <span>08:00-12:00</span><span className="text-slate-400">pauza</span><span>13:00-17:00</span>
          </span>
          <span className="text-slate-600">Program standard (8h)</span>
        </div>
        {[["CO", "Concediu odihnă", "bg-blue-100 text-blue-700 border-blue-300"],
          ["CM", "Concediu medical", "bg-orange-100 text-orange-700 border-orange-300"],
          ["NM", "Nemotivat", "bg-red-100 text-red-700 border-red-300"],
          ["X", "Weekend/Nelucrată", "bg-slate-200 text-slate-500 border-slate-400"]].map(([t, l, c]) => (
          <div key={l} className="flex items-center gap-1.5">
            <span className={`inline-flex items-center justify-center min-w-7 h-5 px-1 rounded border text-[10px] font-semibold ${c}`}>{t}</span>
            <span className="text-slate-600">{l}</span>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="evidenta-table-wrap">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr>
                <th className="sticky left-0 bg-slate-100 z-20 px-2 py-2 text-left font-semibold border-r w-8">Nr.</th>
                <th className="sticky left-8 bg-slate-100 z-20 px-2 py-2 text-left font-semibold border-r min-w-[160px]">Nume și Prenume</th>
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const day = i + 1;
                  const we = isWeekend(day), hol = isHoliday(day);
                  const d = new Date(year, month - 1, day).getDay();
                  return (
                    <th key={day} title={hol ? holidayDays[day] : ""} className={`px-1 py-1 text-center font-medium border-r min-w-[58px] ${hol ? "bg-violet-300 text-violet-900" : we ? "bg-slate-300 text-slate-600" : "bg-slate-100"}`}>
                      <div className="text-[9px] text-slate-500">{DAY_LETTERS[d]}</div>
                      <div>{day}</div>
                    </th>
                  );
                })}
                <th className="sticky right-0 bg-green-100 z-20 px-2 py-2 text-center font-semibold border-l-2 border-green-300 min-w-[48px]">Total Ore</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={daysInMonth + 3} className="text-center py-10 text-slate-400">Niciun angajat în evidență pentru această lună.</td></tr>
              ) : rows.map((emp, idx) => (
                <tr key={emp.id} className="border-b hover:bg-slate-50" data-testid={`evidenta-row-${emp.id}`}>
                  <td className="sticky left-0 bg-white z-10 px-2 py-1 text-center border-r font-medium text-slate-500">{idx + 1}</td>
                  <td className="sticky left-8 bg-white z-10 px-2 py-1 border-r font-semibold text-[#0B1424] whitespace-nowrap">{displayName(emp)}</td>
                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const day = i + 1;
                    const c = getCell(emp, day);
                    let content = null, cls = "bg-white";
                    if (c.type === "work") {
                      content = (
                        <div className="flex flex-col leading-tight text-[8px] text-slate-700">
                          {c.lines.map((ln, li) => <span key={li} className={ln === "pauza" ? "text-slate-400 text-[7px]" : ""}>{ln}</span>)}
                        </div>
                      );
                      cls = "bg-green-50";
                    } else if (c.type === "code") {
                      content = <span className="text-[10px] font-semibold">{c.code}</span>;
                      cls = codeClass(c.code);
                    } else if (c.type === "x") {
                      content = <span className="text-[10px]">X</span>;
                      cls = isHoliday(day) ? "bg-violet-200 text-violet-700" : "bg-slate-200 text-slate-500";
                    }
                    return (
                      <td key={day} data-testid={`evidenta-cell-${emp.id}-${day}`} className={`px-0.5 py-1 text-center border-r ${cls}`}>
                        {content}
                      </td>
                    );
                  })}
                  <td className="sticky right-0 bg-green-50 z-10 px-2 py-1 text-center font-semibold text-green-800 border-l-2 border-green-300" data-testid={`evidenta-total-${emp.id}`}>{totalFor(emp) || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
